import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { useGetOwnUserQuery, useSignInMutation } from '../../../../features/auth/authApiSlice'
import { setCredentials } from '../../../../features/auth/authSlice'
import * as Request from '../../../../pkg/requests'
import useOwnUser from '../../../../hooks/useOwnUser'
import useServerResponse from 'hooks/useServerResponse'
import { useTranslation } from 'react-i18next'
import {
  LoginPageContainer,
  LeftPanel,
  RightPanel,
  CompanyLogo,
  WelcomeText,
  WelcomeTitle,
  WelcomeSubtitle,
  LoginFormContainer,
  LoginTitle,
  LoginSubtitle,
  InputContainer,
  StyledInput,
  CheckboxContainer,
  StyledCheckbox,
  RememberMeText,
  AlreadyMemberText,
  SubmitButton,
  FormRow,
} from './signInElements'

const SignInView: React.FC = () => {
  const { t } = useTranslation()
  const [signIn] = useSignInMutation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const signedUser = useOwnUser()
  const [isSigned, setIsSigned] = useState<boolean>(false)
  const ownUser = useGetOwnUserQuery({}, { skip: !isSigned })
  const { setResponseMessage, setShowResponse } = useServerResponse()

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false,
  })

  const [errors, setErrors] = useState({
    username: '',
    password: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (ownUser.data) {
      signedUser.setUserUID(ownUser.data.id)
      signedUser.setCompanyProfiles(ownUser.data.company_user_profiles)
      navigate('/market')
    }
  }, [ownUser.data])

  const validateForm = () => {
    let valid = true
    const newErrors = { username: '', password: '' }

    if (!formData.username) {
      newErrors.username = t('Root:Login.Required')
      valid = false
    }

    if (!formData.password) {
      newErrors.password = t('Root:Login.Required')
      valid = false
    } else if (formData.password.length < 4) {
      newErrors.password = t('Root:Login.TooShort')
      valid = false
    }

    setErrors(newErrors)
    return valid
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      const { username, password } = formData
      const userData = await signIn({ username, password }).unwrap()
      dispatch(setCredentials({ ...userData, username }))
      setIsSigned(true)
    } catch (err: any) {
      if (err?.response?.status === 500) {
        setResponseMessage(t('Root:Login.ServerError'))
        setShowResponse(true)
      } else {
        setResponseMessage(t('Root:Login.InvalidLogin'))
        setShowResponse(true)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <LoginPageContainer>
      <LeftPanel>
        <CompanyLogo>WHARF</CompanyLogo>
        <WelcomeText>
          <WelcomeSubtitle>{t('Root:Login.WelcomeSubtitle')}</WelcomeSubtitle>
          <WelcomeTitle>{t('Root:Login.WelcomeBack')}</WelcomeTitle>
          <p>{t('Root:Login.WelcomeDescription')}</p>
        </WelcomeText>
      </LeftPanel>

      <RightPanel>
        <LoginFormContainer onSubmit={handleSubmit}>
          <LoginTitle>{t('Root:Login.Login')}</LoginTitle>
          <LoginSubtitle>{t('Root:Login.Subtitle')}</LoginSubtitle>

          <InputContainer>
            <StyledInput
              type='text'
              name='username'
              placeholder={t('Root:Login.UsernamePlaceholder')}
              value={formData.username}
              onChange={handleChange}
            />
          </InputContainer>

          <InputContainer>
            <StyledInput
              type='password'
              name='password'
              placeholder={t('Root:Login.PasswordPlaceholder')}
              value={formData.password}
              onChange={handleChange}
            />
          </InputContainer>

          <FormRow>
            <CheckboxContainer>
              <StyledCheckbox
                type='checkbox'
                name='rememberMe'
                id='rememberMe'
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              <RememberMeText htmlFor='rememberMe'>{t('Root:Login.RememberMe')}</RememberMeText>
            </CheckboxContainer>
          </FormRow>

          <SubmitButton
            type='submit'
            disabled={isSubmitting}
          >
            {t('Root:Login.SubmitButton')}
          </SubmitButton>
        </LoginFormContainer>
      </RightPanel>
    </LoginPageContainer>
  )
}

export default SignInView
